/**
 * Query hooks for the app list.
 *
 * Changing an app changes what the whole shell may reach, so a successful
 * install or uninstall does two things beyond refetching this list: it
 * invalidates the session bootstrap, which is where the route gate reads
 * install state from, and it drops every other cached query. Rows belonging to
 * an app that has just been removed must not stay on screen looking live.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/api/query";
import type { AppCode } from "@/lib/modules";

import { installApp, listApps, uninstallApp, type AppRecord } from "./api";

export const appKeys = {
  all: ["platform/apps"] as const,
  list: () => ["platform/apps", "list"] as const,
};

export function useApps(enabled = true) {
  return useQuery<AppRecord[]>({
    queryKey: appKeys.list(),
    queryFn: async ({ signal }) => (await listApps(signal)).items,
    enabled,
  });
}

export interface AppChange {
  code: AppCode;
  expectedVersion?: number;
}

export function useInstallApp() {
  return useAppMutation((change) => installApp(change.code, change.expectedVersion));
}

export function useUninstallApp() {
  return useAppMutation((change) => uninstallApp(change.code, change.expectedVersion));
}

function useAppMutation(run: (change: AppChange) => Promise<AppRecord>) {
  const queryClient = useQueryClient();
  return useMutation<AppRecord, unknown, AppChange>({
    mutationFn: run,
    onSuccess: async () => {
      // The session carries `installed_modules`, and it is cached with
      // staleTime Infinity — nothing refetches it on its own, so this
      // invalidation is what makes the route gate and the menu agree with what
      // just happened.
      await queryClient.invalidateQueries({ queryKey: queryKeys.session.current() });
      queryClient.removeQueries({
        predicate: (entry) =>
          entry.queryKey[0] !== "session" && entry.queryKey[0] !== "platform/apps",
      });
      await queryClient.invalidateQueries({ queryKey: appKeys.all });
    },
  });
}
